/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import List from '@ba-ui-toolkit/list';
import { classnames as cx, listHelper, propsUtils } from '@ba-ui-toolkit/utils';
import RadioGroup from '@ba-ui-toolkit/radiogroup';
import CheckboxGroup from '@ba-ui-toolkit/checkboxgroup';
import { ToggleSwitchGroup } from '@ba-ui-toolkit/toggleswitchgroup';
import Label from '@ba-ui-toolkit/label';
import Accordion, { AccordionItem } from '@ba-ui-toolkit/accordion';
import VSpacer from '@ba-ui-toolkit/vspacer';
import Separator from '@ba-ui-toolkit/separator';
import Container from '@ba-ui-toolkit/container';
import menuStyles from './Menu.scss';

const MenuClassName = menuStyles.menuClassName.replace(/"/g, '');
const { sanitize } = propsUtils;

function isPropsValid(props) {
	if (!props.name) {
		console.error('A name property must be specified for each Menu Section.'); // eslint-disable-line no-console
	}
}

function getGroupedSection(sectionContent, label, accordion, type) {
	const { list, radio, checkbox, toggleswitch } = type;

	return (
		<div>
			{accordion ? (
				<div>{sectionContent}</div>
			) : (
				<div
					className={cx(`${MenuClassName}__grouped-section`, {
						list,
						radio,
						checkbox,
						toggleswitch,
					})}
				>
					{label && <Label type="caption" label={label} />}
					{sectionContent}
				</div>
			)}
		</div>
	);
}

function isSelected(itemProps, index) {
	const { selected, name, content } = itemProps;

	return selected[name] === content[index].value;
}

function onChangeList(itemProps, event) {
	const { content, onChange, selected, name } = itemProps;
	const index = listHelper.singleSelect(event, content, selected[name]);
	const item = content[index];
	onChange && onChange(name, item.value, event.disabled);
}

function onChangeCheckboxOrToggle(content, label, name, checkboxSelected, onChange) {
	const selectedIndex = content.map(item => item.label).indexOf(label);
	const valueToChange = content[selectedIndex].value;
	checkboxSelected[valueToChange] = !checkboxSelected[valueToChange];
	onChange && onChange(name, checkboxSelected);
}

export default class Menu extends Component {
	static propTypes = {
		/** Sub-components of Menu to display (i.e. Menu.List, Menu.Radio, etc.) */
		children: PropTypes.node,
		/** Function that is called when a value in the Menu changes (name and value are arguments) */
		onChange: PropTypes.func,
		/** Object that defines which items from each sub-component are selected */
		selected: PropTypes.object,
		/** Defines the position of where the menu should be fixed on the screen */
		position: PropTypes.shape({
			x: PropTypes.number.isRequired,
			y: PropTypes.number.isRequired,
		}),
		/** The width of the menu */
		width: PropTypes.string,
		/** The height of the menu */
		height: PropTypes.string,
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Custom CSS styling */
		style: PropTypes.object,
	};

	static defaultProps = {
		selected: {},
		className: '',
		style: {},
	};

	constructor(props) {
		super(props);

		this.state = {
			top: 0,
			left: 0,
		};
	}

	componentDidMount() {
		if (this._container) this._getPosition();
	}

	componentDidUpdate(prevProps) {
		// If now there is positioning and previously there wasn't
		if (this.props.position && !prevProps.position) {
			this._getPosition();
			// Or if there is positioning, and previously there was, check if that positioning has changed and if so update the position
		} else if (this.props.position) {
			if (
				prevProps.position.x !== this.props.position.x ||
				prevProps.position.y !== this.props.position.y ||
				prevProps.width !== this.props.width
			) {
				this._getPosition();
			}
		}
	}

	// This is being done this way to mimic the behaviour of context menus in Glass
	_getPosition() {
		const { position } = this.props;
		const containerRect = this._container._node.getBoundingClientRect();
		const width = containerRect.width + 3;
		const height = containerRect.height + 3;

		let left;
		if (window.innerWidth - position.x < width) {
			left = window.innerWidth - width;
		} else {
			left = position.x;
		}

		let top;
		if (window.innerHeight - position.y < height) {
			top = window.innerHeight - height;
		} else {
			top = position.y;
		}

		this.setState({ top, left });
	}

	static Separator(props) {
		return <Separator data-tid="ba-common-menu__separator" {...props} />;
	}

	static Spacer(props) {
		return <VSpacer data-tid="ba-common-menu__spacer" {...props} />;
	}

	static Accordion(props) {
		const { onChange, selected, children, ...passThrough } = props;

		return (
			<Accordion
				data-tid="ba-common-menu__accordion"
				{...passThrough}
				className={`${MenuClassName}__grouped-section`}
			>
				{Children.map(children, child => (
					<AccordionItem
						data-tid={`ba-common-menu__accordion-item-${child.props.name}`}
						id={`ba-accordion-item-1-${child.props.name}`}
						itemName={child.props.label}
						icon="left"
					>
						{cloneElement(child, { onChange, selected, accordion: true })}
					</AccordionItem>
				))}
			</Accordion>
		);
	}

	static List(props) {
		isPropsValid(props);
		const {
			content,
			accordion,
			label,
			onChange,
			name,
			selected,
			clickSelection,
			...passThrough
		} = props;

		const listProps = {
			'data-tid': 'ba-common-menu__list',
			...passThrough,
			options: content,
			isSelected: index => isSelected({ content, onChange, selected, name }, index),
			onSelectionChange: event => onChangeList(props, event),
			length: content.length,
			initialIndex: 0,
			selectionMode: 'single',
			tabIndex: 0,
			clickSelection,
		};

		const sectionContent = <List {...listProps} />;

		return getGroupedSection(sectionContent, label, accordion, { list: true });
	}

	static Radio(props) {
		isPropsValid(props);
		const { content, accordion, label, onChange, selected, name, ...passThrough } = props;

		const radioProps = {
			'data-tid': 'ba-common-menu__radios',
			...passThrough,
			options: content,
			direction: 'vertical',
			checked: selected[name],
			onChange: value => onChange(name, value),
		};

		const sectionContent = <RadioGroup {...radioProps} />;

		return getGroupedSection(sectionContent, label, accordion, { radio: true });
	}

	static Checkbox(props) {
		isPropsValid(props);
		const { content, accordion, label, onChange, selected, name, ...passThrough } = props;

		const checkboxSelected = selected[name] ? { ...selected[name] } : {};

		// Conversion for object value being set to true to label being set to true for checkboxgroup
		const parsedCheckboxSelected = {};
		for (let i = 0; i < content.length; i++) {
			if (checkboxSelected[content[i].value]) parsedCheckboxSelected[content[i].label] = true;
		}

		const checkboxProps = {
			'data-tid': 'ba-common-menu__checkboxes',
			...passThrough,
			options: content,
			direction: 'vertical',
			selected: parsedCheckboxSelected,
			onChange: value =>
				onChangeCheckboxOrToggle(content, value, name, checkboxSelected, onChange),
		};

		const sectionContent = <CheckboxGroup {...checkboxProps} />;

		return getGroupedSection(sectionContent, label, accordion, { checkbox: true });
	}

	static ToggleSwitch(props) {
		isPropsValid(props);
		const { content, accordion, label, onChange, selected, name, ...passThrough } = props;

		const ToggleSwitchSelected = selected[name] ? { ...selected[name] } : {};

		const parsedToggleSwitchSelected = {};
		for (let i = 0; i < content.length; i++) {
			if (ToggleSwitchSelected[content[i].value]) {
				parsedToggleSwitchSelected[content[i].label] = true;
			}
		}

		const toggleswitchprops = {
			'data-tid': 'ba-common-menu__toggleswitches',
			...passThrough,
			options: content,
			direction: 'vertical',
			selected: parsedToggleSwitchSelected,
			small: true,
			onChange: value =>
				onChangeCheckboxOrToggle(content, value, name, ToggleSwitchSelected, onChange),
		};

		const sectionContent = <ToggleSwitchGroup {...toggleswitchprops} />;

		return getGroupedSection(sectionContent, label, accordion, { toggleswitch: true });
	}

	_containerRef = node => {
		this._container = node;
	};

	render() {
		const {
			children,
			onChange,
			selected,
			position,
			width,
			height,
			className,
			style,
			...others
		} = this.props;
		const { top, left } = this.state;
		const classNames = cx(MenuClassName);
		const contentWidth = position ? '100%' : width;
		const contentHeight = position ? '100%' : height;
		const content = (
			<div
				className={`${classNames} ${className}`}
				style={{ width: contentWidth, height: contentHeight }}
			>
				{Children.map(
					children,
					child =>
						child &&
						cloneElement(child, {
							onChange,
							selected,
						})
				)}
			</div>
		);

		const styles = {
			position: 'fixed',
			top: `${top}px`,
			left: `${left}px`,
			...style,
		};

		return position ? (
			<Container
				{...sanitize(others, Menu)}
				className={`${MenuClassName}__container ${className}`}
				width={width}
				gutter={[0, 0]}
				height={height}
				style={styles}
				ref={this._containerRef}
			>
				{content}
			</Container>
		) : (
			content
		);
	}
}
